import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles, CheckSquare, Users, Mail, FileText, MessageSquare, Bell, Rocket, BookOpen, UserCog, AtSign, Award, Palette, DollarSign, Megaphone, HandHeart, Flame, GraduationCap, Gift, HeartHandshake, TrendingUp, Library } from "lucide-react";
import { MyProfile } from "./Profile";

import HealthDashboard from "./HealthDashboard";
import StewardIdentity from "./StewardIdentity";

import { LaunchWizard, Checklist, LaunchPanel } from "./admin/wizard";
import { Drops, Articles } from "./admin/content";
import { Members, Leads, StewardOfStewards } from "./admin/crm";
import { DailyHeatTab, FreeGroupTab, AdCopyTab, TestimonialsAdmin } from "./admin/marketing";
import { Summary, CommunityAdmin, Reminders, EmailTemplates, Outbox } from "./admin/comms";
import { BrandTab, PricingTab, NotifPrefsTab } from "./admin/settings";
import { DiscipleshipTab } from "./admin/discipleship";
import { BlessingsTab, ServiceBoardTab } from "./admin/blessings";
import { IncreaseTab } from "./admin/increase";
import { LibraryTab } from "./admin/library";
import ErrorBoundary from "../components/ErrorBoundary";

const TABS = [
  { id: "health", label: "Health", icon: Award },
  { id: "increase", label: "Kingdom Increase", icon: TrendingUp },
  { id: "wizard", label: "Launch Wizard", icon: Rocket },
  { id: "identity", label: "Steward Identity", icon: HandHeart },
  { id: "checklist", label: "Checklist", icon: CheckSquare },
  { id: "drops", label: "Drops", icon: Sparkles },
  { id: "articles", label: "Articles", icon: BookOpen },
  { id: "discipleship", label: "Diagnostics & Paths", icon: GraduationCap },
  { id: "testimonials", label: "Testimonials", icon: Award },
  { id: "ad-copy", label: "Ad Copy", icon: Megaphone },
  { id: "steward-stewards", label: "Steward of Stewards", icon: HandHeart },
  { id: "blessings", label: "Blessings", icon: Gift },
  { id: "service", label: "Acts of Service", icon: HeartHandshake },
  { id: "members", label: "Members", icon: Users },
  { id: "leads", label: "Leads", icon: AtSign },
  { id: "free-group", label: "Free Group", icon: Users },
  { id: "summary", label: "Summary", icon: FileText },
  { id: "community", label: "Community", icon: MessageSquare },
  { id: "reminders", label: "Reminders", icon: Bell },
  { id: "emails", label: "Emails", icon: Mail },
  { id: "daily-heat", label: "Daily Heat", icon: Flame },
  { id: "email-log", label: "Email Log", icon: Mail },
  { id: "brand", label: "Brand & Visuals", icon: Palette },
  { id: "pricing", label: "Pricing & Offers", icon: DollarSign },
  { id: "notif-prefs", label: "My Notifications", icon: Bell },
  { id: "profile", label: "Profile", icon: UserCog },
  { id: "launch", label: "Launch", icon: Rocket },
];

export default function Admin() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("tab") || "health";
  const [tab, setTab] = useState(initial);
  const switchTab = (id) => { setTab(id); params.set("tab", id); setParams(params); };
  useEffect(() => { if (params.get("tab")) setTab(params.get("tab")); }, [params]);

  return (
    <div className="min-h-screen px-6 lg:px-10 py-12">
      <div className="max-w-7xl mx-auto">
        <div className="overline mb-3">// ROBIN'S PORTAL</div>
        <h1 className="font-display text-5xl text-cream">Creator Control Room</h1>
        <p className="text-textMuted mt-2">Every operation. Every drop. Every member. One pane.</p>

        <div className="flex flex-wrap gap-px bg-borderGold mt-10 mb-10">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => switchTab(t.id)}
              className={`bg-void px-5 py-3 flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] transition-colors ${tab === t.id ? "text-gold" : "text-textMuted hover:text-cream"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        <ErrorBoundary key={tab}>
        {tab === "health" && <HealthDashboard />}
        {tab === "increase" && <IncreaseTab />}
        {tab === "library" && <LibraryTab />}
        {tab === "identity" && <StewardIdentity />}
        {tab === "wizard" && <LaunchWizard onJump={switchTab}/>}
        {tab === "checklist" && <Checklist />}
        {tab === "drops" && <Drops />}
        {tab === "articles" && <Articles />}
        {tab === "discipleship" && <DiscipleshipTab />}
        {tab === "testimonials" && <TestimonialsAdmin />}
        {tab === "ad-copy" && <AdCopyTab />}
        {tab === "steward-stewards" && <StewardOfStewards />}
        {tab === "blessings" && <BlessingsTab />}
        {tab === "service" && <ServiceBoardTab />}
        {tab === "members" && <Members />}
        {tab === "leads" && <Leads />}
        {tab === "free-group" && <FreeGroupTab />}
        {tab === "summary" && <Summary />}
        {tab === "community" && <CommunityAdmin />}
        {tab === "reminders" && <Reminders />}
        {tab === "emails" && <EmailTemplates />}
        {tab === "daily-heat" && <DailyHeatTab />}
        {tab === "email-log" && <Outbox />}
        {tab === "brand" && <BrandTab />}
        {tab === "pricing" && <PricingTab />}
        {tab === "notif-prefs" && <NotifPrefsTab />}
        {tab === "profile" && <MyProfile />}
        {tab === "launch" && <LaunchPanel />}
        </ErrorBoundary>
      </div>
    </div>
  );
}
